import { useEffect, useRef, useState } from 'react';
import {
  getGridSectionBottomSpacerVh,
  getGridSectionFrame,
  getDesktopZoomImageFrame,
  getPrimaryZoomImageDimensions,
  clampZoomImageLeft,
  SCROLL_PHASE_CAPTION_END,
  SCROLL_PHASE_MIDDLE_ZOOM_END,
  SCROLL_PHASE_ZOOM1_END,
  SCROLL_PHASE_ZOOM2_END,
  PORTFOLIO_PIN_THRESHOLD_PX,
} from '../portfolioGridData';

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function segmentProgress(total, start, end) {
  if (total <= start) {
    return 0;
  }

  if (total >= end) {
    return 1;
  }

  return (total - start) / (end - start);
}

function smoothstep(edge0, edge1, value) {
  if (value <= edge0) {
    return 0;
  }

  if (value >= edge1) {
    return 1;
  }

  const t = (value - edge0) / (edge1 - edge0);

  return t * t * (3 - 2 * t);
}

function getPinPoint(section) {
  return section.offsetTop;
}

/**
 * @param {number} gridReturnProgress
 */
export function getPortfolioPinFadeOpacity(gridReturnProgress) {
  return 1 - smoothstep(0.55, 1, gridReturnProgress);
}

/**
 * @param {number} gridReturnProgress
 */
export function getPortfolioPinTranslateYPercent(gridReturnProgress) {
  const slideProgress = segmentProgress(gridReturnProgress, 0.3, 1);

  return -smoothstep(0, 1, slideProgress) * 100;
}

/**
 * @param {HTMLElement} section
 * @param {HTMLElement} track
 * @param {boolean} isMobile
 */
export function computePortfolioScrollState(section, track, isMobile) {
  const scrollY = window.scrollY;
  const pinPoint = getPinPoint(section);
  const sectionRect = section.getBoundingClientRect();
  const viewport = window.innerHeight;
  const trackHeight = track.offsetHeight;
  const spacerHeight = viewport * (getGridSectionBottomSpacerVh(isMobile) / 100);
  const heroHeight = viewport;
  const animationScrollLength = heroHeight + trackHeight + spacerHeight;
  const scrollEnd = pinPoint + animationScrollLength;
  const zoomRange = animationScrollLength;
  const resetProgress = {
    zoom1Progress: 0,
    middleZoomProgress: 0,
    captionProgress: 0,
    zoom2Progress: 0,
    gridReturnProgress: 0,
  };

  if (sectionRect.bottom <= 0) {
    return {
      isPinned: false,
      showPinOverlay: false,
      showInlineGrid: false,
      pinOpacity: 0,
      gridFullyHidden: true,
      ...resetProgress,
    };
  }

  if (sectionRect.top > PORTFOLIO_PIN_THRESHOLD_PX || scrollY < pinPoint) {
    return {
      isPinned: false,
      showPinOverlay: false,
      showInlineGrid: true,
      pinOpacity: 0,
      gridFullyHidden: true,
      ...resetProgress,
    };
  }

  const progress = clamp((scrollY - pinPoint) / zoomRange, 0, 1);
  const gridReturnProgress = segmentProgress(progress, SCROLL_PHASE_ZOOM2_END, 1);
  const pinOpacity = getPortfolioPinFadeOpacity(gridReturnProgress);
  const pastScrollEnd = scrollY >= scrollEnd;

  return {
    isPinned: !pastScrollEnd,
    showPinOverlay: true,
    showInlineGrid: false,
    pinOpacity,
    gridFullyHidden: pastScrollEnd || pinOpacity <= 0.01,
    zoom1Progress: segmentProgress(progress, 0, SCROLL_PHASE_ZOOM1_END),
    middleZoomProgress: segmentProgress(progress, SCROLL_PHASE_ZOOM1_END, SCROLL_PHASE_MIDDLE_ZOOM_END),
    captionProgress: segmentProgress(progress, SCROLL_PHASE_MIDDLE_ZOOM_END, SCROLL_PHASE_CAPTION_END),
    zoom2Progress: segmentProgress(progress, SCROLL_PHASE_CAPTION_END, SCROLL_PHASE_ZOOM2_END),
    gridReturnProgress,
  };
}

/**
 * @param {boolean} isMobile
 */
export function readPortfolioGridHandoff(isMobile) {
  const section = document.getElementById('portfolio');
  const track = section?.querySelector('[data-portfolio-scroll-track]');

  if (!section || !track) {
    return { pinOpacity: 0, gridFullyHidden: true };
  }

  const state = computePortfolioScrollState(section, track, isMobile);

  return {
    pinOpacity: state.pinOpacity,
    gridFullyHidden: state.gridFullyHidden,
  };
}

/**
 * @param {React.RefObject<HTMLElement | null>} sectionRef
 * @param {React.RefObject<HTMLElement | null>} trackRef
 * @param {boolean} isMobile
 */
export function usePortfolioZoomScroll(sectionRef, trackRef, isMobile) {
  const [state, setState] = useState({
    isPinned: false,
    showPinOverlay: false,
    showInlineGrid: true,
    zoom1Progress: 0,
    middleZoomProgress: 0,
    captionProgress: 0,
    zoom2Progress: 0,
    gridReturnProgress: 0,
  });
  const rafRef = useRef(null);
  const stateRef = useRef(state);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    const section = sectionRef.current;
    const track = trackRef.current;

    if (!section || !track) {
      setState({
        isPinned: false,
        showPinOverlay: false,
        showInlineGrid: false,
        zoom1Progress: 0,
        middleZoomProgress: 0,
        captionProgress: 0,
        zoom2Progress: 0,
        gridReturnProgress: 0,
      });
      return undefined;
    }

    const update = () => {
      rafRef.current = null;

      if (!section || !track) {
        return;
      }

      const nextState = computePortfolioScrollState(section, track, isMobile);
      setState({
        isPinned: nextState.isPinned,
        showPinOverlay: nextState.showPinOverlay,
        showInlineGrid: nextState.showInlineGrid,
        zoom1Progress: nextState.zoom1Progress,
        middleZoomProgress: nextState.middleZoomProgress,
        captionProgress: nextState.captionProgress,
        zoom2Progress: nextState.zoom2Progress,
        gridReturnProgress: nextState.gridReturnProgress,
      });
    };

    const onScroll = () => {
      if (!section || !track) {
        return;
      }

      const nextState = computePortfolioScrollState(section, track, isMobile);
      const pinBoundaryChanged =
        nextState.showPinOverlay !== stateRef.current.showPinOverlay ||
        nextState.showInlineGrid !== stateRef.current.showInlineGrid;

      if (pinBoundaryChanged) {
        if (rafRef.current !== null) {
          window.cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }

        setState({
          isPinned: nextState.isPinned,
          showPinOverlay: nextState.showPinOverlay,
          showInlineGrid: nextState.showInlineGrid,
          zoom1Progress: nextState.zoom1Progress,
          middleZoomProgress: nextState.middleZoomProgress,
          captionProgress: nextState.captionProgress,
          zoom2Progress: nextState.zoom2Progress,
          gridReturnProgress: nextState.gridReturnProgress,
        });
        return;
      }

      if (rafRef.current !== null) {
        return;
      }

      rafRef.current = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', update);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', update);

      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
      }
    };
  }, [sectionRef, trackRef, isMobile]);

  return state;
}

/**
 * @param {number} viewportWidth
 * @param {number} viewportHeight
 * @param {boolean} isMobile
 */
export function getZoomTargetFrame(viewportWidth, viewportHeight, isMobile) {
  const { width: sectionWidth, left: sectionLeft } = getGridSectionFrame(viewportWidth, isMobile);

  if (!isMobile) {
    const desktopFrame = getDesktopZoomImageFrame(
      sectionWidth,
      sectionLeft,
      viewportWidth,
      viewportHeight
    );

    return {
      ...desktopFrame,
      headerSpace: viewportHeight * 0.28,
    };
  }

  const headerSpace = viewportHeight * 0.36;
  const baseWidth = sectionWidth;
  const baseHeight = Math.min(viewportHeight * 0.48, baseWidth / (4 / 3));
  const { targetWidth, targetHeight } = getPrimaryZoomImageDimensions(
    baseWidth,
    baseHeight,
    viewportWidth,
    viewportHeight
  );
  const targetLeft = clampZoomImageLeft(
    sectionLeft + (baseWidth - targetWidth) / 2,
    targetWidth,
    viewportWidth
  );
  const targetTop = headerSpace + 20;

  return {
    targetWidth,
    targetHeight,
    targetLeft,
    targetTop,
    headerSpace,
    textColumnWidth: sectionWidth,
    textColumnLeft: sectionLeft,
  };
}

/**
 * @param {number} cols
 * @param {number} rowCount
 * @param {number} tileWidth
 * @param {number} tileHeight
 * @param {number} gap
 * @param {number} gridOffsetX
 * @param {number} viewportHeight
 * @param {number} index
 */
export function getTileStartRect(
  cols,
  rowCount,
  tileWidth,
  tileHeight,
  gap,
  gridOffsetX,
  viewportHeight,
  index
) {
  const col = index % cols;
  const row = Math.floor(index / cols);
  const blockHeight = rowCount * tileHeight + (rowCount - 1) * gap;
  const blockTop = (viewportHeight - blockHeight) / 2;

  return {
    left: gridOffsetX + col * (tileWidth + gap),
    top: blockTop + row * (tileHeight + gap),
    width: tileWidth,
    height: tileHeight,
  };
}

/**
 * @param {number} viewportWidth
 * @param {number} viewportHeight
 * @param {number} tileWidth
 * @param {number} tileHeight
 */
export function getBottomRightCornerRect(viewportWidth, viewportHeight, tileWidth, tileHeight) {
  const pad = 28;

  return {
    left: viewportWidth - pad - tileWidth,
    top: viewportHeight - pad - tileHeight,
    width: tileWidth,
    height: tileHeight,
  };
}

/**
 * @param {number} viewportWidth
 * @param {number} viewportHeight
 * @param {number} tileWidth
 * @param {number} tileHeight
 */
export function getBottomLeftCornerRect(_viewportWidth, viewportHeight, tileWidth, tileHeight) {
  const pad = 28;

  return {
    left: pad,
    top: viewportHeight - pad - tileHeight,
    width: tileWidth,
    height: tileHeight,
  };
}

/** @deprecated use getTileStartRect */
export function getLastTileStartRect(
  cols,
  rowCount,
  tileWidth,
  tileHeight,
  gap,
  padding,
  gridOffsetX,
  viewportHeight,
  lastIndex
) {
  return getTileStartRect(cols, rowCount, tileWidth, tileHeight, gap, gridOffsetX, viewportHeight, lastIndex);
}

/**
 * @param {{ left: number; top: number; width: number; height: number }} start
 * @param {{ targetLeft: number; targetTop: number; targetWidth: number; targetHeight: number }} target
 * @param {number} progress
 */
export function interpolateZoomRect(start, target, progress) {
  return {
    left: start.left + (target.targetLeft - start.left) * progress,
    top: start.top + (target.targetTop - start.top) * progress,
    width: start.width + (target.targetWidth - start.width) * progress,
    height: start.height + (target.targetHeight - start.height) * progress,
  };
}
